import { AxiosError } from 'axios';
import { useAuthStore } from '../stores/auth';
import type { MenuRight, ModuleMenu, MenuItem } from '../types/menu';
import { http } from './http';

export interface Module {
  id: number;
  name: string;
  icon: string;
  color: string;
  isAccessible: boolean;
  isDeleted: boolean;
  type?: 'mopui' | 'moceroui'; // Add type to distinguish between applications
  description?: string; // Add description for better UX
}

interface RawMenuItem {
  Id: number;
  Name: string;
  Icon?: string;
  Path?: string;
  ParentId?: number;
  Order?: number;
  IsActive: boolean;
}

interface ApiResponse<T> {
  result: T;
  ErrorInfo: {
    ErrorCode: string | null;
    ErrorMessage: string | null;
    ErrorType: string | null;
  };
}

const API_PREFIX = '/PlatformCoreServices/Authorize';

// Helper function to handle API responses
function handleApiResponse<T>(response: ApiResponse<T>): T {
  console.log('API Response:', response);

  if (response.ErrorInfo?.ErrorMessage) {
    throw new Error(response.ErrorInfo.ErrorMessage);
  }
  if (!response.result) {
    throw new Error('Invalid API response: missing result');
  }
  return response.result;
}

export const moduleService = {

  async loadNews() {
    try {

      const rolesData: any = localStorage.getItem('RolesData');

      let additionalData = JSON.parse(rolesData);
      let roles;

      const response = await http.get<ApiResponse<any[]>>(
        `${API_PREFIX}/NewsEvents/ListByRoleId?RoleId=${additionalData.RoleId}`
      );

      return response;
    } catch (error) {
      console.error('Error fetching user modules:', error);
      throw error;
    }
  },
  async loadTasks() {
    try {

      let rolesData: any = localStorage.getItem('RolesData');
      rolesData = JSON.parse(rolesData);

      let UsersData: any = localStorage.getItem('UsersData');
      UsersData = JSON.parse(UsersData);

      const response = await http.get<ApiResponse<any[]>>(
        `${API_PREFIX}/Common/GetTaskList?clientId=${UsersData.OrganizationId}&branchId=${UsersData.BranchId}&isAdmin=${UsersData.Admin}&isDepartmentHead=false&userId=${UsersData.UserId}&DeptRoleMapId=${rolesData.DeptRoleMapId}&TaskTypeId=0&TaskStatusId=0&TaskPriorityId=0&DepartmentId=0&Assignee=0&SLAStatus=0&Search=&TaskCategory=1&IsContact=false&PageNumber=1&PageSize=3`
      );

      return response;
    } catch (error) {
      console.error('Error fetching user modules:', error);
      throw error;
    }
  },
  async getUserModules(organizationId: number): Promise<Module[]> {
    try {
      console.log('Fetching modules for organization:', organizationId);

      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const token = localStorage.getItem('token');
      const rolesData = localStorage.getItem('RolesData');
      let UsersData: any = localStorage.getItem('UsersData');
      let additionalData = JSON.parse(UsersData);
      let roles;
      if (!token) {
        throw new Error('No authentication token found');
      }

      let headers: Record<string, string> = {
        'X-Auth-Token': token
      };

      // Add session ID if available
      if (rolesData) {
        try {
          roles = JSON.parse(rolesData);
          if (roles.DeptRoleMapId) {
            headers['SessionID'] = roles.DeptRoleMapId.toString();
          }
        } catch (e) {
          console.error('Error parsing roles data:', e);
        }
      }

      const response = await http.get<ApiResponse<any[]>>(
        `${API_PREFIX}/ModuleMaster/GetModulesByUser?OrganizationId=${organizationId}&DeptRoleMapId=${roles.DeptRoleMapId}&IsAdmin=${additionalData.Admin}`,
        { headers }
      );

      console.log('Raw API response:', response);
      const result = handleApiResponse(response);
      console.log('Raw modules data:', result);

      const mappedModules = this.mapModulesResponse(result);

      // Enhance modules with application type detection
      const enhancedModules = this.enhanceModulesWithAppType(mappedModules);
      console.log('Enhanced modules with app types:', enhancedModules);

      return enhancedModules;
    } catch (error) {
      console.error('Error fetching user modules:', error);
      if (error instanceof AxiosError) {
        console.log('API Error Response:', error.response?.data);
        if (error.response?.status === 401) {
          const authStore = useAuthStore();
          authStore.clearAuth();
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(error.response?.data?.ErrorInfo?.ErrorMessage || 'Failed to load modules');
      }
      throw error;
    }
  },

  // Enhanced method to determine application type and add descriptions
  enhanceModulesWithAppType(modules: Module[]): Module[] {
    return modules.map(module => {
      const enhanced = { ...module };

      // Determine application type based on module name/id
      const moceroUIModules = ['CRM', 'Inventory', 'Billing', 'Communication', 'Alert Engine', 'Queue Management', 'User Management', 'Authorization'];
      const mopuiModules = ['Accounts', 'Asset Management', 'Task Management'];

      if (moceroUIModules.some(name => module.name.toLowerCase().includes(name.toLowerCase()))) {
        enhanced.type = 'moceroui';
      } else if (mopuiModules.some(name => module.name.toLowerCase().includes(name.toLowerCase()))) {
        enhanced.type = 'mopui';
      } else {
        // Default to MOPUI for new modules
        enhanced.type = 'mopui';
        enhanced.description = 'Modern platform module';
      }

      return enhanced;
    });
  },

  // Session bridge for MoceroUI navigation
  async prepareSessionForMoceroUI(module: Module): Promise<void> {
    try {
      console.log('Preparing session for MoceroUI module:', module.name);

      const authStore = useAuthStore();
      const user = authStore.user;

      if (!user) {
        throw new Error('No user data available for session preparation');
      }

      // Get additional user data from localStorage if available
      const existingUsersData = localStorage.getItem('UsersData');
      let additionalData: any = {};

      if (existingUsersData) {
        try {
          additionalData = JSON.parse(existingUsersData);
        } catch (e) {
          console.warn('Could not parse existing UsersData:', e);
        }
      }

      // alert('yes')
      // debugger;
      // debugger;

      // Create MoceroUI-compatible user data
      const moceroUIUsersData = {
        OrganizationId: user.organizationId,
        OrganizationCode: additionalData.OrganizationCode || 'ORG001',
        BranchId: user.branchId,
        BranchCode: additionalData.BranchCode || 'BR001',
        UserId: user.id,
        UserName: user.name,
        UserStatus: true,
        phoneNumber: additionalData.phoneNumber || '',
        HospitalId: user.organizationId,
        HospitalCode: additionalData.HospitalCode || 'HOSP001',
        HospitalBranchId: additionalData.HospitalBranchId || user.branchId,
        HospitalBranchCode: additionalData.HospitalBranchCode || 'HBR001',
        ApplicationType: 3, // MoceroUI application type
        CountryId: additionalData.CountryId || 1,
        AgentNumber: additionalData.AgentNumber || '',
        ProductId: additionalData.ProductId || 1,
        AddressVar: additionalData.AddressVar || '',
        BranchStateId: additionalData.BranchStateId || 1,
        HelpUrl: additionalData.HelpUrl || '',
        Admin: additionalData.Admin,
        AgentId: additionalData.AgentId == null ? 0 : additionalData.AgentId,
        AgentStatus: additionalData.AgentStatus,
        AgentDid: additionalData.AllowedDidNumbers == null ? 0 : additionalData.AllowedDidNumbers,
        TaxType: additionalData.TaxType,
        IsRequestExpired: additionalData.IsRequestExpired,
        ExpiredDuration: additionalData.ExpiredDuration,

        ...additionalData
      };

      // Create MoceroUI-compatible role data
      const moceroUIRolesData = {
        RoleId: user.roleId,
        RoleName: this.getRoleName(user.roleId),
        DeptRoleMapId: user.deptRoleMapId
      };

      // Store data in MOPUI auth store for MoceroUI to access
      authStore.setMoceroUIUsersData(moceroUIUsersData);
      authStore.setMoceroUIRolesData(moceroUIRolesData);

      // Store module-specific data
      localStorage.setItem('SelectedModuleId', module.id.toString());
      localStorage.setItem('SelectedModuleName', module.name);
      localStorage.setItem('CrossAppNavigation', 'true');
      localStorage.setItem('SourceApp', 'MOPUI');

      console.log('Session prepared for MoceroUI:', {
        userData: moceroUIUsersData,
        roleData: moceroUIRolesData,
        moduleId: module.id
      });

    } catch (error) {
      console.error('Error preparing session for MoceroUI:', error);
      throw new Error('Failed to prepare session for module navigation');
    }
  },

  // Navigate to MoceroUI module with dynamic route loading
  navigateToMoceroUI(module: Module): void {
    // Check if we're in development (different ports) or production (unified server)
    const isDevelopment = window.location.port === '5173';

    let moceroUIUrl: string;

    if (isDevelopment) {
      // Development: Use different port
      moceroUIUrl = 'http://localhost:5174';
    } else {
      // Production: Use same server with /moceroui/ path
      moceroUIUrl = `${window.location.origin}/moceroui`;
    }

    // Prepare dynamic routing data for MoceroUI
    this.prepareDynamicRouting(module);

    // Get the session data from MOPUI auth store

    const authStore = useAuthStore();
    const usersData = authStore.moceroUIUsersData;
    const rolesData = authStore.moceroUIRolesData;

    // Navigate to MoceroUI with module information and session data in URL parameters
    const urlParams = new URLSearchParams({
      moduleId: module.id.toString(),
      moduleName: module.name,
      crossApp: 'true',
      sourceApp: 'MOPUI',
      dynamicRouting: 'true'
    });

    // Add session data as URL parameters (base64 encoded to handle special characters)
    if (usersData) {
      try {
        // alert('Noo');
        // debugger;
        // debugger;

        const encodedUsersData = btoa(JSON.stringify(usersData));
        urlParams.set('usersData', encodedUsersData);

        console.log('UsersData encoded successfully, length:', encodedUsersData.length);
      } catch (error) {
        console.error('Error encoding usersData:', error);
      }
    } else {
      console.warn('No usersData available for cross-app navigation');
    }

    if (rolesData) {
      try {
        const encodedRolesData = btoa(JSON.stringify(rolesData));
        urlParams.set('rolesData', encodedRolesData);
        console.log('RolesData encoded successfully, length:', encodedRolesData.length);
      } catch (error) {
        console.error('Error encoding rolesData:', error);
      }
    } else {
      console.warn('No rolesData available for cross-app navigation');
    }

    const targetUrl = `${moceroUIUrl}/#/?${urlParams.toString()}`;

    console.log(`Navigating to MoceroUI with dynamic routing (${isDevelopment ? 'development' : 'production'}):`, targetUrl);
    console.log(`Module to be loaded dynamically: ${module.name} (ID: ${module.id})`);
    console.log('URL Parameters being sent:');
    console.log('- moduleId:', urlParams.get('moduleId'));
    console.log('- moduleName:', urlParams.get('moduleName'));
    console.log('- usersData:', urlParams.get('usersData') ? 'Present' : 'Missing');
    console.log('- rolesData:', urlParams.get('rolesData') ? 'Present' : 'Missing');

    // Use window.location.href to navigate
    window.location.href = targetUrl;
  },

  // Prepare dynamic routing data for MoceroUI
  prepareDynamicRouting(module: Module): void {
    try {
      console.log('Preparing dynamic routing for module:', module.name);

      // Store module information for dynamic loading (still needed for session data)
      localStorage.setItem('SelectedModuleId', module.id.toString());
      localStorage.setItem('SelectedModuleName', module.name);
      localStorage.setItem('CrossAppNavigation', 'true');
      localStorage.setItem('SourceApp', 'MOPUI');
      localStorage.setItem('TargetApp', 'MoceroUI');
      localStorage.setItem('DynamicRouting', 'true');

      // Store module-specific routing preferences
      const routingPreferences = this.getModuleRoutingPreferences(module.name);
      if (routingPreferences) {
        localStorage.setItem('ModuleRoutingPreferences', JSON.stringify(routingPreferences));
      }

      console.log('Dynamic routing data prepared:', {
        moduleId: module.id,
        moduleName: module.name,
        routingPreferences
      });

    } catch (error) {
      console.error('Error preparing dynamic routing:', error);
    }
  },

  // Get module-specific routing preferences
  getModuleRoutingPreferences(moduleName: string): any {
    const preferences: { [key: string]: any } = {
      'crm': {
        defaultRoute: '/Layout',
        layoutComponent: 'Layout',
        loadMasters: true
      },
      'inventory': {
        defaultRoute: '/InventoryLayout',
        layoutComponent: 'InventoryLayout',
        loadMasters: true
      },
      'billing': {
        defaultRoute: '/BillingLayout',
        layoutComponent: 'BillingLayout',
        loadMasters: false
      },
      'user management': {
        defaultRoute: '/userLayout',
        layoutComponent: 'UserLayout',
        loadMasters: true
      },
      'authorization': {
        defaultRoute: '/userLayout',
        layoutComponent: 'UserLayout',
        loadMasters: true
      },
      'alert engine': {
        defaultRoute: '/AlertEngineLayout',
        layoutComponent: 'AlertEngineLayout',
        loadMasters: false
      },
      'communication': {
        defaultRoute: '/CommunicationLayout',
        layoutComponent: 'CommunicationLayout',
        loadMasters: false
      },
      'queue management': {
        defaultRoute: '/Layout',
        layoutComponent: 'Layout',
        loadMasters: false
      }
    };

    return preferences[moduleName.toLowerCase()] || {
      defaultRoute: '/Layout',
      layoutComponent: 'Layout',
      loadMasters: false
    };
  },

  // Get role name by ID (you may need to enhance this based on your role mapping)
  getRoleName(roleId: number): string {
    const roleMap: { [key: number]: string } = {
      1: 'Super Admin',
      2: 'Admin',
      3: 'Manager',
      4: 'User',
      5: 'Viewer'
    };
    return roleMap[roleId] || 'User';
  },

  // Get MoceroUI module descriptions
  getMoceroUIModuleDescription(moduleName: string): string {
    const descriptions: { [key: string]: string } = {
      'CRM': 'Customer Relationship Management - Legacy Interface',
      'Inventory': 'Inventory Management System - Legacy Interface',
      'Billing': 'Billing and Invoice Management - Legacy Interface',
      'Communication': 'Communication Management - Legacy Interface',
      'Alert Engine': 'Alert and Notification System - Legacy Interface',
      'Queue Management': 'Queue Management System - Legacy Interface',
      'User Management': 'User Management System - Legacy Interface'
    };
    return descriptions[moduleName] || 'Legacy Mocero Platform Module';
  },

  // Get MOPUI module descriptions
  getMOPUIModuleDescription(moduleName: string): string {
    const descriptions: { [key: string]: string } = {
      'Accounts': 'Financial Accounting and Management',
      'Authorization': 'User Authorization and Permissions',
      'Asset Management': 'Asset Tracking and Management',
      'Task Management': 'Task and Project Management'
    };
    return descriptions[moduleName] || 'Modern Platform Module';
  },

  async getModuleRights(roleId: number, moduleId: number): Promise<MenuRight[]> {
    try {
      console.log('Fetching rights for role:', roleId, 'module:', moduleId);

      if (!roleId || !moduleId) {
        throw new Error('Role ID and Module ID are required');
      }

      const token = localStorage.getItem('token');
      const rolesData = localStorage.getItem('RolesData');
      if (!token) {
        throw new Error('No authentication token found');
      }

      let headers: Record<string, string> = {
        'X-Auth-Token': token
      };

      if (rolesData) {
        try {
          const roles = JSON.parse(rolesData);
          if (roles.DeptRoleMapId) {
            headers['SessionID'] = roles.DeptRoleMapId.toString();
          }
        } catch (e) {
          console.error('Error parsing roles data:', e);
        }
      }

      const response = await http.get<ApiResponse<any>>(
        `${API_PREFIX}/EntityOperationsDeptRoleMap/GetRightsByRoleIdModuleId?RoleId=${roleId}&ModuleId=${moduleId}`,
        { headers }
      );

      const result = handleApiResponse(response);
      return this.mapRightsResponse(result);
    } catch (error) {
      console.error('Error fetching module rights:', error);
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          const authStore = useAuthStore();
          authStore.clearAuth();
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(error.response?.data?.ErrorInfo?.ErrorMessage || 'Failed to load module rights');
      }
      throw error;
    }
  },

  async getModuleMenu(moduleId: number): Promise<ModuleMenu> {
    try {
      console.log('Fetching menu for module:', moduleId);

      if (!moduleId || typeof moduleId !== 'number') {
        throw new Error('Valid Module ID is required');
      }

      const token = localStorage.getItem('token');
      const rolesData = localStorage.getItem('RolesData');
      if (!token) {
        throw new Error('No authentication token found');
      }

      let headers: Record<string, string> = {
        'X-Auth-Token': token
      };

      if (rolesData) {
        try {
          const roles = JSON.parse(rolesData);
          if (roles.DeptRoleMapId) {
            headers['SessionID'] = roles.DeptRoleMapId.toString();
          }
        } catch (e) {
          console.error('Error parsing roles data:', e);
        }
      }

      const response = await http.get<ApiResponse<{ Menus: RawMenuItem[]; SubMenus: RawMenuItem[] }>>(
        `${API_PREFIX}/Menu/GetMenuSubMenuList?Type=${moduleId}`,
        { headers }
      );

      const result = handleApiResponse(response);

      // Validate response data
      if (!result || typeof result !== 'object') {
        throw new Error('Invalid menu data received from server');
      }

      if (!Array.isArray(result.Menus) || !Array.isArray(result.SubMenus)) {
        console.warn('Invalid menu structure received:', result);
        return { menus: [], subMenus: [] };
      }

      return this.mapMenuResponse(result);
    } catch (error) {
      console.error('Error fetching module menu:', error);
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          const authStore = useAuthStore();
          authStore.clearAuth();
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(error.response?.data?.ErrorInfo?.ErrorMessage || 'Failed to load module menu');
      }
      throw error;
    }
  },

  mapModulesResponse(data: any[]): Module[] {
    if (!Array.isArray(data)) {
      console.warn('Expected array for modules data, received:', typeof data);
      return [];
    }

    return data.map(module => {
      if (!module) return null;

      const mapped = {
        id: module.ID || module.ModuleId,
        name: module.Name || module.ModuleName,
        icon: module.ImagePath,
        color: this.getModuleColor(module.Name || module.ModuleName),
        isAccessible: module.IsActive,
        isDeleted: module.IsDeleted || false
      };
      console.log('Mapped module:', mapped);
      return mapped;
    }).filter(Boolean) as Module[];
  },

  mapRightsResponse(data: any): MenuRight[] {
    if (!data) return [];

    return Object.entries(data).map(([menuId, rights]: [string, any]) => ({
      MenuId: parseInt(menuId),
      hasAccess: rights.hasAccess,
      operations: Array.isArray(rights.operations) ? rights.operations : []
    }));
  },

  mapMenuResponse(data: { Menus: RawMenuItem[]; SubMenus: RawMenuItem[] }): ModuleMenu {
    if (!data || !Array.isArray(data.Menus) || !Array.isArray(data.SubMenus)) {
      console.warn('Invalid menu data structure:', data);
      return { menus: [], subMenus: [] };
    }

    const mapMenuItem = (item: RawMenuItem): MenuItem | null => {
      if (!item || typeof item.Id !== 'number' || !item.Name) {
        console.warn('Invalid menu item:', item);
        return null;
      }

      return {
        id: item.Id,
        name: item.Name,
        icon: item.Icon || '',
        path: item.Path || '',
        parentId: item.ParentId,
        order: item.Order || 0,
        isActive: item.IsActive,
        children: []
      };
    };

    const menus: MenuItem[] = data.Menus
      .map(mapMenuItem)
      .filter(Boolean) as MenuItem[];

    const subMenus: MenuItem[] = data.SubMenus
      .map(mapMenuItem)
      .filter(Boolean) as MenuItem[];

    // Build menu hierarchy
    const menuMap = new Map<number, MenuItem>();
    menus.forEach((menu: MenuItem) => menuMap.set(menu.id, menu));

    subMenus.forEach((subMenu: MenuItem) => {
      if (subMenu.parentId) {
        const parentMenu = menuMap.get(subMenu.parentId);
        if (parentMenu) {
          if (!parentMenu.children) {
            parentMenu.children = [];
          }
          parentMenu.children.push(subMenu);
        }
      }
    });

    return {
      menus,
      subMenus
    };
  },

  getModuleIcon(moduleName: string): string {
    const iconMap: { [key: string]: string } = {
      'Accounts': 'pi pi-wallet',
      'Inventory': 'pi pi-box',
      'CRM': 'pi pi-users',
      'Communication': 'pi pi-comments',
      'Billing': 'pi pi-money-bill',
      'Authorization': 'pi pi-lock',
      'Hospital': 'pi pi-heart',
      'Doctor': 'pi pi-user-doctor',
      'Patient': 'pi pi-user',
      'Nurse': 'pi pi-heart-fill',
      'Pharmacy': 'pi pi-prescription',
      'License': 'pi pi-verified',
      'Common': 'pi pi-cog'
    };

    return iconMap[moduleName] || 'pi pi-box';
  },

  getModuleColor(moduleName: string): string {
    const colorMap: { [key: string]: string } = {
      'Accounts': '#FF9800',
      'Inventory': '#4CAF50',
      'CRM': '#2196F3',
      'Communication': '#9C27B0',
      'Billing': '#F44336',
      'Authorization': '#607D8B',
      'Hospital': '#00BCD4',
      'Doctor': '#3F51B5',
      'Patient': '#E91E63',
      'Nurse': '#8BC34A',
      'Pharmacy': '#FF5722',
      'License': '#795548',
      'Common': '#9E9E9E'
    };

    return colorMap[moduleName] || '#9E9E9E';
  }
};
